import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Hakkımızda — Türkçe.
 *
 * Ana sayfadaki insan/ajan dörtlüsü burada tekrar edilmiyor, İngilizcesinde
 * olduğu gibi: bir farklılaştırıcıyı iki tık arayla iki kez söylemek onu
 * zayıflatır.
 */
export const about: WrittenPage = {
  navLabel: 'Hakkımızda',
  title: 'Digitopia Hakkında',
  description:
    'Digitopia neden var, neye inanıyor ve kim yönetiyor. 2019\'da kuruldu. New York, Londra, İstanbul. 40+ ülkede 300+ müşteri.',
  sections: [
    {
      kind: 'hero',
      // Zaten büyük harfle yazılıyor, çünkü CSS'in uppercase'i sayfa dili
      // Türkçe olunca "i" harfini "İ" yapıyor ve marka adı "DİGİTOPİA" diye
      // çıkıyordu (2026-08-27). Türkçe kelimeler cümle düzeninde kalabilir —
      // onların büyük harfi zaten doğru.
      eyebrow: 'DIGITOPIA HAKKINDA',
      headline: 'Biz Digitopian\'larız.',
      sub: 'Yazılımın tavsiye vermeyi bırakıp eylemeye başladığı an için kurulmuş bir şirket.',
      photo: {
        src: '/photo/open-office-team.jpg',
        alt: 'Açık ofiste birlikte çalışan Digitopia ekibi; arkadaki beyaz tahtada bir plan taslağı.',
      },
    },
    {
      kind: 'prose',
      heading: 'Neden varız',
      paragraphs: [
        'Digitopia, ileri görüşlü şirketlerin iş sonuçlarını en üst düzeye çıkarmak için ajan tabanlı yapay zeka çözümlerini Forward Deployed Engineer ekipleri ve yapay zeka ajanlarıyla hayata geçirir.',
        'Danışmanlık şirketlere ne yapacağını söyledi ve gitti. Yazılım araç verdi ve gitti. Hiçbiri güvenilir biçimde bir sonuç üretmedi. Eksik olduğunu düşündüğümüz şirketi kurduk: muhakemenin önemli olduğu yerde insan muhakemesi, olmadığı her yerde ajan tabanlı uygulama ve bir kurumun altına imza atabileceği bir güven standardı.',
      ],
    },
    {
      kind: 'features',
      columns: 3,
      items: [
        {
          title: 'İnsan rehberliği',
          body: '[Strateji danışmanları](/en/solutions/strategy-consulting), [dönüşüm yöneticileri](/en/solutions/transformation-management), Forward Deployed Engineer\'lar, AI mühendisleri.',
        },
        {
          title: 'İş çözümleri',
          body: '[Ajan tabanlı yapay zeka çerçeveleri](/en/solutions/enterprise-ai-solutions), iş ontolojileri, iş ajanları, teknoloji ajanları.',
        },
        {
          title: 'Güven',
          body: '[Gizlilik](/en/privacy-policy), uyum, yönetişim, veri egemenliği.',
        },
      ],
      footnote: 'Çoğu şirket bu üçünden birini satar. Sonuç üçünü birlikte gerektirir.',
    },
    {
      kind: 'features',
      heading: 'Amaç, misyon, vizyon',
      columns: 3,
      textSize: 'lg',
      items: [
        {
          title: 'Amaç',
          note: '#ScaleValue',
          body: 'Çalıştığımız şirketler ve içindeki insanlar için ölçeklenen değer.',
        },
        {
          title: 'Misyon',
          note: '#TransformBetter',
          body: 'Dönüşümü daha iyi yapmak: ölçülmüş, sıraya konmuş ve gerçekten tamamlanmış.',
        },
        {
          title: 'Vizyon',
          body: 'Gelecek bir dijital ütopya; biz ona Digitopia diyoruz.',
        },
      ],
    },
    {
      kind: 'stats',
      heading: 'Kısaca',
      items: [
        { figure: '300+', label: 'müşteri' },
        { figure: '50+', label: 'iş ortağı' },
        { figure: '600+', label: 'uygulayıcı' },
        { figure: '40+', label: 'ülke' },
        { figure: '20+', label: 'sektör' },
        { figure: '3.000+', label: 'ölçüm' },
      ],
      footnote: '2019\'da kuruldu. New York. Londra. İstanbul.',
    },
    {
      kind: 'features',
      heading: 'Üç CEO. Üç odak.',
      columns: 3,
      items: [
        {
          title: 'Halil Aksu',
          note: 'Odak: Büyüme',
          // 15.09.2026: yeniden LinkedIn. İletişim sayfasına yalnızca QR kod
          // okutularak gelinmesi istendi, sitede ona giden bir link kalmadı.
          href: 'https://www.linkedin.com/in/halilaksu/',
          photo: {
            src: '/photo/halil-aksu-portrait.webp',
            alt: 'Halil Aksu, Digitopia Eş-CEO',
          },
        },
        {
          title: 'Kagan Sahin',
          note: 'Odak: Operasyon',
          // 15.09.2026: yukarıdaki kartla aynı sebeple yeniden LinkedIn —
          // iletişim sayfası bir QR kod adresi, sitenin yönlendirdiği bir yer değil.
          href: 'https://www.linkedin.com/in/kagansahin/',
          photo: {
            src: '/photo/kagan-sahin-portrait.webp',
            alt: 'Kagan Sahin, Digitopia Eş-CEO',
          },
        },
        {
          title: 'Ahmet Bilgen',
          note: 'Odak: Teknoloji',
          // 15.09.2026: yukarıdaki iki kartla aynı sebeple yeniden LinkedIn —
          // iletişim sayfası bir QR kod adresi, sitenin yönlendirdiği bir yer değil.
          href: 'https://www.linkedin.com/in/abilgen/',
          photo: {
            src: '/photo/ahmet-bilgen-portrait.jpg',
            alt: 'Ahmet Bilgen, Digitopia Eş-CEO',
          },
        },
      ],
      check:
        'Growth/Operations/Technology sırası, kaldırılan alt başlıktaki üç odak sırasıyla ' +
        'mevcut kurucu listesi eşleştirilerek verildi — hangi eş-CEO\'nun hangi alanı ' +
        'sahiplendiği doğrulanmadı.',
    },
    // İngilizcesiyle aynı (31.08.2026): değerler burada tek bir banda indi,
    // sekiz değerin kendisi `content/pages/tr/our-values.ts` sayfasına taşındı.
    // Hem görsel hem buton o sayfayı açıyor.
    {
      kind: 'split',
      flip: true,
      heading: 'Karanlığa ışık taşırız.',
      paragraphs: ['Değerlerimizi okuyun…'],
      cta: { label: 'Değerlerimiz', href: '/en/about/our-values' },
      photo: {
        src: '/photo/axolotl-our-values.webp',
        alt: 'Digitopia aksolotlu; mor-pembe gradyan çerçeveli koyu bir panelin içinde.',
      },
      photoHref: '/en/about/our-values',
      photoFit: 'contain',
    },
    {
      kind: 'prose',
      heading: 'Muhakeme gereken yerde muhakeme. Geri kalan her yerde uygulama.',
      paragraphs: [
        'Danışmanlık şirketleri problemlere insan atar. Yazılım şirketleri elinize bir platform verir. İkisi de ne sattığı konusunda dürüsttür ve hiçbiri tek başına sizi sonuca götürmez, çünkü muhakeme ile ölçek aynı kabiliyet değildir.',
        'Biz ikisini birlikte işletiyoruz. Danışmanlar çerçeveyi kurar ve insan gerektiren kararları verir. Ajanlar altta yükü taşır; aynı standartta, her seferinde. 300+ şirketten gelen örüntüler çerçevelere geri akar, yani bizden aldığınız şey siz aldıktan sonra da iyileşir.',
      ],
    },
    {
      kind: 'cta',
      heading: 'Bir Digitopian olun.',
      sub: 'Cesaret, sahiplik ve merak arıyoruz. Gerisi öğrenilebilir ve öğrenmenize yardım ederiz.',
      tone: 'band',
      headingHref: LINKS.careers,
      ctas: [{ label: 'Açık pozisyonlara bakın', href: LINKS.careers }],
    },
    // İngilizcesiyle aynı: sayfayı kapatan siyah "Sonucunuzu konuşalım." bandı
    // kaldırıldı (2026-08-29). Footer'ın üstündeki gradient ContactBand zaten
    // her sayfada duruyor, bölüm sırası iki dilde birebir kalsın diye.
  ],
}
